import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { MenuItem } from '@mui/material';

import { CfsApi } from '@/api';
import { IUser, Role } from '@/models';
import { isTargetRole, validateRoute } from '@/services';
import { useUserStore } from '@/store';
import { openNewWindow } from '@/utils';

import {
  CFSCaseAlreadyExistsModal,
  ExistingCaseMode,
} from './CFSCaseAlreadyExistsModal';
interface CFSCreateRmsMenuItemProps {
  cfsId?: string;
}
export const CFSCreateRmsMenuItem = ({ cfsId }: CFSCreateRmsMenuItemProps) => {
  const { user, userAgencyId, account } = useUserStore();
  const navigate = useNavigate();
  const [isChecking, setIsChecking] = useState(false);
  const [existingCase, setExistingCase] = useState<{
    recordId: string;
    recordNumber: string;
    agencyId?: string;
    mode: ExistingCaseMode;
  } | null>(null);

  const handleClick = async () => {
    if (!cfsId || isChecking) return;
    setIsChecking(true);
    try {
      const response = await CfsApi.getRelatedRecords(String(cfsId));
      const caseRecord = (response.data?.records || []).find(
        (record) => record.recordType === 'CASE',
      );
      if (caseRecord) {
        const isDifferentAgency =
          Boolean(caseRecord.agency) &&
          Boolean(userAgencyId) &&
          caseRecord.agency !== userAgencyId;

        const userHasCasesAccess = validateRoute(
          '/cases',
          user as IUser,
          account ?? undefined,
        );
        const noPermission =
          Boolean(caseRecord.isRestricted) || !userHasCasesAccess;

        const mode: ExistingCaseMode = isDifferentAgency
          ? 'different-agency'
          : noPermission
          ? 'no-permission'
          : 'same-agency';

        setExistingCase({
          recordId: caseRecord.recordId,
          recordNumber: caseRecord.recordNumber,
          agencyId: isDifferentAgency ? caseRecord.agency : undefined,
          mode,
        });
        return;
      }
      navigate(`/cases?from=${cfsId}`);
    } finally {
      setIsChecking(false);
    }
  };

  const handleCreateNew = () => {
    setExistingCase(null);
    navigate(`/cases?from=${cfsId}`);
  };

  const handleViewCase = () => {
    if (!existingCase) return;
    void openNewWindow(
      `/cases/${existingCase.recordId}/overview`,
      existingCase.recordId,
    );
    setExistingCase(null);
  };

  if (!isTargetRole(user as IUser, Role.RMS_CASES_FULL_ACCESS) || !cfsId)
    return null;

  return (
    <>
      <MenuItem onClick={handleClick}>Create RMS Case</MenuItem>
      {existingCase && (
        <CFSCaseAlreadyExistsModal
          open
          onClose={() => setExistingCase(null)}
          onCreateNew={handleCreateNew}
          onViewCase={
            existingCase.mode === 'same-agency' ? handleViewCase : undefined
          }
          caseNumber={existingCase.recordNumber}
          agencyId={existingCase.agencyId}
          mode={existingCase.mode}
        />
      )}
    </>
  );
};
